import { memo } from 'react';
import { type Control, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getComponentByTypeForFilters } from 'components/common/Table/tableFunctions';
import { getRadioButtonItems, toRadioButtonOptions } from 'utils/formItem/radioButtonChoice';
import { TABLE_CELL_TYPE } from 'types/enums';
import type { Option } from 'components/mui/common/Autocomplete';
import type { UserAgendaTableDataSchema } from 'types';

interface DataSchema extends UserAgendaTableDataSchema {
  translationKey?: string;
  options?: string[];
  allowMultiple?: boolean;
  statusOptions?: Option[];
}

interface Props {
  control: Control<FieldValues>;
  item: DataSchema;
  isDisabled?: boolean;
}

export const GenericFieldComponent = memo(({ control, item, isDisabled = false }: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();

  const getComponent = () => {
    const radioButtonOptions: Option[] | undefined =
      item.type === TABLE_CELL_TYPE.TEXT && item.originalType === 'RadioButton'
        ? toRadioButtonOptions(getRadioButtonItems(item.formItemDefinition), i18n.language).map(({ label, value }) => ({
            label,
            value,
          }))
        : undefined;

    return getComponentByTypeForFilters({
      originalType: item?.formItemDefinition?.type ?? item.type,
      type: item.type,
      control,
      isDisabled,
      registerName: item?.label,
      allowMultiple: item.allowMultiple,
      statusOptions: item?.statusOptions ?? undefined,
      radioButtonOptions,
      label: item?.customKey ? item?.customKey : t(item?.translationKey ?? ''),
      ...(item.type === TABLE_CELL_TYPE.DATE && { dateMask: item.dateMask }),
      formItemDefinition: item.formItemDefinition,
      options: item.options,
      formItemId: item.label,
    });
  };

  return <>{getComponent()}</>;
});
